import translate from './translate';
import {api} from '../../../api/api';
import removeMd from 'remove-markdown';
import {isWeb} from '../../responsive';
import http from '../../../api/http';

interface TranslateOptions {
  from?: string;
  to?: string;
  tld?: string;
  client?: string;
}

export const translateFunction = (
  value: string | string[],
  options: TranslateOptions,
): Promise<string[]> => {
  let text: string[];
  if (typeof value === 'string') {
    text = [value];
  } else {
    text = value;
  }
  return translate(text, options);
};

const sortObjectByKey = (obj: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).sort(([a], [b]) => a.localeCompare(b)),
  );
};

export const translateTableArticle = async (
  languageCode: string,
  tableObjValues: any,
  excludeKeys: string[] = [],
  originalLang: string = 'en',
) => {
  if (isWeb) {
    return await translateTableArticleWeb(
      languageCode,
      tableObjValues,
      excludeKeys,
      originalLang,
    );
  } else {
    return await translateTableArticleApp(
      languageCode,
      tableObjValues,
      excludeKeys,
      originalLang,
    );
  }
};

/**
 * Translates the values of an object to the specified language, excluding certain keys.
 *
 * @param languageCode
 * @param {object} tableObj - The object containing values to be translated.
 * @param {string[]} excludeKeys - An array of keys to be excluded from translation.
 * @param {string} originalLang - The original language code.
 * @returns {Promise<object>} - The translated object with original values for excluded keys.
 */
export const translateTableArticleApp = async (
  languageCode: string,
  tableObjValues: any,
  excludeKeys: string[] = [],
  originalLang: string = 'en',
): Promise<any> => {
  const tableObj = sortObjectByKey(tableObjValues);
  try {
    const tableKeys = Object.keys(tableObj);

    // Filter out keys that are in the excludeKeys array
    const filteredKeys = tableKeys.filter(
      key =>
        !excludeKeys.includes(key) &&
        !Array.isArray(tableObj[key]) &&
        !!tableObj[key],
    );

    // Get the values for the keys that are not excluded
    let filteredValues: string[] = [];
    filteredKeys.forEach(key => {
      if (Array.isArray(tableObj[key]) || !tableObj[key]) {
        return;
      }
      filteredValues.push(tableObj[key]?.replace(/\s+/g, ' ').trim());
    });

    if (languageCode === originalLang) {
      return tableObj;
    }

    // Translate the encoded values
    const result = await translateFunction(filteredValues, {
      to: languageCode,
    });

    // Create the translated result object
    const translatedResult = (result || []).reduce(
      (acc: any, value: string, index: number) => {
        acc[filteredKeys[index]] = value;
        return acc;
      },
      {},
    );

    // Add the excluded keys with their original values back to the result
    excludeKeys.forEach(key => {
      translatedResult[key] = tableObj[key];
    });

    return translatedResult || {};
  } catch (e) {
    return tableObj;
  }
};

/**
 * Translates the values of an object to the specified language, excluding certain keys.
 * Translates each field individually to avoid mixing up translations.
 *
 * @param languageCode - The target language code to translate to
 * @param {object} tableObj - The object containing values to be translated
 * @param {string[]} excludeKeys - An array of keys to be excluded from translation
 * @param {string} originalLang - The original language code
 * @returns {Promise<object>} - The translated object with original values for excluded keys
 */
export const translateTableArticleWeb = async (
  languageCode: string,
  tableObj: any,
  excludeKeys: string[] = [],
  originalLang = 'en',
): Promise<Record<string, string>> => {
  try {
    // Get the original keys and preserve their order
    const keys = Object.keys(tableObj);

    // Use a Map to guarantee key order preservation
    const resultMap = new Map<string, string>();

    // Initialize all keys with their original values
    keys.forEach(k => {
      resultMap.set(k, tableObj[k]?.toString() || '');
    });

    // Skip translation if same language or no keys to translate
    if (languageCode === originalLang || keys.length === 0) {
      return Object.fromEntries(resultMap);
    }

    // Filter keys that need translation
    const keysToTranslate = keys.filter(
      k =>
        !excludeKeys.includes(k) &&
        tableObj[k] !== null &&
        tableObj[k] !== undefined,
    );

    // Translate each field individually to avoid mixing up translations
    try {
      // Use Promise.all to translate all fields in parallel
      await Promise.all(
        keysToTranslate.map(async key => {
          try {
            const value = tableObj[key];

            // Prepare a single field for translation
            let valueToTranslate = value;

            // Special handling for summary/description fields
            if (key === 'summary' || key === 'description') {
              valueToTranslate = removeMd(value.toString())
                .replace(/\\n/g, '') // remove all `\n` escaped slashes
                .replace(/\s*\\\n\s*/g, '\n') // remove literal `\\\n` with a newline
                .replace(/\n{2,}/g, '\n\n') // normalize multiple newlines
                .trim();
            }

            // Send single-field translation request
            const singleFieldPayload = {
              languageCode,
              tableObj: {[key]: valueToTranslate},
              excludeKeys: [],
              originalLang,
            };

            const response = await http.post(
              `https://translation-six.vercel.app/${api.translate.googleTranslate}`,
              JSON.stringify(singleFieldPayload),
              {'Content-Type': 'application/json'},
              false,
            );

            // Process the response for this single field
            // Handle both standard HttpResponse format and direct object format
            if (
              response &&
              response.content &&
              typeof response.content === 'object'
            ) {
              // Standard HttpResponse format
              if (response.content[key]) {
                const translatedValue = response.content[key];
                resultMap.set(key, translatedValue.replace(/\[\]/g, ''));
              }
            } else if (response && typeof response === 'object') {
              // Direct response format (as returned by the web API)
              const directResponse = response as unknown as Record<
                string,
                string
              >;
              if (directResponse[key]) {
                const translatedValue = directResponse[key];
                resultMap.set(key, translatedValue.replace(/\[\]/g, ''));
              }
            }
          } catch (fieldError) {
            // On error, keep original value (already in resultMap)
          }
        }),
      );

      // Ensure excluded keys have original values
      excludeKeys.forEach(k => {
        if (keys.includes(k)) {
          resultMap.set(k, tableObj[k]?.toString() || '');
        }
      });

      return Object.fromEntries(resultMap);
    } catch (translationError) {
      // On general translation error, Map already has original values
    }

    // Convert Map to plain object, preserving key order
    return Object.fromEntries(resultMap);
  } catch (globalError) {
    // In case of catastrophic error, return original object
    return tableObj;
  }
};
